import { ConfigProvider, Drawer, Grid, Layout } from 'antd';
import zhTW from 'antd/locale/zh_TW';
import { CSSProperties, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { adminLinks } from '@/components/Admin/adminNav';
import Footer from '@/components/Admin/Footer';
import Header from '@/components/Admin/Header';
import Sidebar from '@/components/Admin/Sidebar';

const { Content, Sider } = Layout;
const { useBreakpoint } = Grid;

const SIDER_WIDTH = 232;
const SIDER_COLLAPSED_WIDTH = 72;
const STORAGE_KEY = 'admin-sidebar-collapsed';

const readCollapsed = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const themeVars = {
  '--admin-bg': '#f4f6f3',
  '--admin-surface': '#ffffff',
  '--admin-border': '#e8ece5',
} as CSSProperties;

export const AdminLayout = () => {
  const screens = useBreakpoint();
  // 第一次 render 時 screens 可能還是空的，此時當作桌機版處理
  const isMobile = screens.lg === false;
  const location = useLocation();

  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(collapsed));
    } catch {
      // 無痕模式等情況無法寫入，忽略即可
    }
  }, [collapsed]);

  // 換頁後自動收起手機版選單
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const handleToggle = () => {
    if (isMobile) {
      setDrawerOpen(true);
    } else {
      setCollapsed((prev) => !prev);
    }
  };

  const siderOffset = isMobile
    ? 0
    : collapsed
      ? SIDER_COLLAPSED_WIDTH
      : SIDER_WIDTH;

  return (
    <ConfigProvider
      locale={zhTW}
      theme={{
        cssVar: true,
        hashed: false,
        token: {
          colorPrimary: '#70915c',
          colorBgLayout: '#f4f6f3',
          colorBorderSecondary: '#e8ece5',
          borderRadius: 8,
          borderRadiusLG: 12,
          fontSize: 14,
        },
        components: {
          Layout: {
            headerBg: '#ffffff',
            siderBg: '#ffffff',
            bodyBg: '#f4f6f3',
          },
          Menu: {
            itemBorderRadius: 8,
            itemHeight: 42,
            itemSelectedBg: '#e9f0e4',
            itemSelectedColor: '#4d6b3c',
            itemHoverBg: '#f2f6ef',
            groupTitleFontSize: 12,
          },
          Card: {
            boxShadowTertiary: '0 1px 2px rgba(31, 41, 55, 0.04)',
          },
          Table: {
            headerBg: '#f6f8f4',
            headerColor: '#4b5563',
            rowHoverBg: '#f9fbf7',
          },
        },
      }}
    >
      <Layout style={{ minHeight: '100vh', ...themeVars }}>
        {isMobile ? (
          <Drawer
            placement="left"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            width={SIDER_WIDTH}
            closable={false}
            styles={{ body: { padding: 0 } }}
          >
            <Sidebar
              links={adminLinks}
              onNavigate={() => setDrawerOpen(false)}
            />
          </Drawer>
        ) : (
          <Sider
            width={SIDER_WIDTH}
            collapsedWidth={SIDER_COLLAPSED_WIDTH}
            collapsed={collapsed}
            trigger={null}
            theme="light"
            style={{
              position: 'fixed',
              left: 0,
              top: 0,
              bottom: 0,
              overflow: 'auto',
              borderRight: '1px solid var(--admin-border)',
              zIndex: 30,
            }}
          >
            <Sidebar links={adminLinks} collapsed={collapsed} />
          </Sider>
        )}

        <Layout
          style={{
            marginLeft: siderOffset,
            transition: 'margin-left 0.2s',
            minHeight: '100vh',
            minWidth: 0,
            background: 'var(--admin-bg)',
          }}
        >
          <Header
            collapsed={collapsed}
            isMobile={isMobile}
            onToggle={handleToggle}
          />

          <Content
            style={{
              padding: isMobile ? 16 : 24,
              flex: 1,
              minWidth: 0,
              width: '100%',
              maxWidth: 1440,
              margin: '0 auto',
            }}
          >
            <Outlet />
          </Content>

          <Footer />
        </Layout>
      </Layout>
    </ConfigProvider>
  );
};
